import { styled } from "goober";
import { type ReactNode, useLayoutEffect, useRef, useState } from "react";
import { Dropdown } from "./Dropdown";
import { FolderIcon } from "./FolderIcon";
import { Icon, type IconName } from "./Icon";
import { isInAppNavigation } from "./Link";
import { Tab, TabButton, tabStyles } from "./TabHeader";
import { Text } from "./Text";

type Item = {
  key: string;
  label: string;
  icon: IconName;
  badge?: ReactNode;
  shortcut?: string;
} & (
  | { to: string; onSelect?: () => void }
  | { to?: never; onSelect: () => void }
);

function TabIcon({ name, active }: { name: IconName; active: boolean }) {
  return name === "folder" ? (
    <FolderIcon open={active} />
  ) : (
    <Icon name={name} size={16} />
  );
}

const Root = styled("nav")`
  position: relative;
  flex: 1;
  min-width: 40px;
  height: 40px;
  padding: 4px;
  margin: -4px;
  overflow: hidden;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
  min-width: 0;
`;

const Measurements = styled(Row)`
  position: absolute;
  width: max-content;
  visibility: hidden;
  pointer-events: none;
`;

const MeasurementTab = styled("span")`
  ${tabStyles}
  height: 32px;
`;

const MenuSlot = styled("div")`
  display: flex;
  min-width: 0;
  max-width: 100%;
  & > div {
    min-width: 0;
    max-width: 100%;
  }
`;

const Selector = styled("button")`
  ${tabStyles}
  height: 32px;
  min-width: 0;
  max-width: 100%;
  & svg {
    flex-shrink: 0;
  }
`;

export function ResponsiveTabs({
  items,
  activeKey,
  label,
}: {
  items: readonly Item[];
  activeKey: string;
  label: string;
}) {
  const rootRef = useRef<HTMLElement>(null);
  const rowRef = useRef<HTMLDivElement>(null);
  const measurementsRef = useRef<HTMLDivElement>(null);
  const focusOnResizeRef = useRef<string | null>(null);
  const [collapsed, setCollapsed] = useState(false);
  const active = items.find((item) => item.key === activeKey) ?? items[0];

  useLayoutEffect(() => {
    const root = rootRef.current;
    const row = rowRef.current;
    const measurements = measurementsRef.current;
    if (!root || !row || !measurements) return;
    const measure = () => {
      const available = row.clientWidth;
      if (!available) return;
      const widths = Array.from(
        measurements.children,
        (child) => (child as HTMLElement).offsetWidth
      );
      const total =
        widths.reduce((sum, width) => sum + width, 0) +
        Math.max(0, widths.length - 1) * 8;
      const next = total > available;
      if (next === collapsed) return;
      if (
        root.contains(document.activeElement) ||
        root.querySelector('[data-dropdown-open="true"]')
      ) {
        focusOnResizeRef.current =
          document.activeElement?.getAttribute("data-tab-key") ?? activeKey;
      }
      setCollapsed(next);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    for (const child of measurements.children) observer.observe(child);
    return () => observer.disconnect();
  }, [items, activeKey, collapsed]);

  useLayoutEffect(() => {
    const key = focusOnResizeRef.current;
    if (key === null) return;
    focusOnResizeRef.current = null;
    const root = rootRef.current;
    const tab = Array.from(
      root?.querySelectorAll<HTMLElement>("[data-tab-key]") ?? []
    ).find((element) => element.dataset.tabKey === key);
    (tab ?? root?.querySelector<HTMLElement>('[aria-haspopup="menu"]'))?.focus({
      preventScroll: true,
    });
  }, [collapsed]);

  const tabContent = (item: Item) => (
    <>
      <TabIcon name={item.icon} active={item.key === activeKey} />
      <Text variant="text-small-md">{item.label}</Text>
      {item.badge}
    </>
  );

  return (
    <Root ref={rootRef} aria-label={label}>
      <Measurements ref={measurementsRef} aria-hidden inert>
        {items.map((item) => (
          <MeasurementTab key={item.key}>{tabContent(item)}</MeasurementTab>
        ))}
      </Measurements>
      <Row ref={rowRef}>
        {collapsed
          ? active && (
              <MenuSlot>
                <Dropdown
                  align="bottom-left"
                  opener={
                    <Selector
                      type="button"
                      className="active"
                      aria-label={`${label}: ${active.label}`}
                      aria-haspopup="menu"
                    >
                      <TabIcon name={active.icon} active />
                      <Text variant="text-small-md" ellipsis>
                        {active.label}
                      </Text>
                      {active.badge}
                      <Icon name="chevron-down" size={12} />
                    </Selector>
                  }
                  sections={[
                    {
                      items: items.map((item) => ({
                        key: item.key,
                        label: item.label,
                        left: (
                          <TabIcon
                            name={item.icon}
                            active={item.key === activeKey}
                          />
                        ),
                        selected: item.key === activeKey,
                        right:
                          item.key === activeKey ? (
                            <Icon name="check" size={12} />
                          ) : (
                            item.badge
                          ),
                        ...(item.to !== undefined
                          ? {
                              type: "link" as const,
                              to: item.to,
                              onClick: item.onSelect,
                            }
                          : {
                              type: "button" as const,
                              onClick: item.onSelect,
                            }),
                      })),
                    },
                  ]}
                />
              </MenuSlot>
            )
          : items.map((item) => {
              return item.to !== undefined ? (
                <Tab
                  key={item.key}
                  data-tab-key={item.key}
                  to={item.to}
                  className={item.key === activeKey ? "active" : undefined}
                  aria-current={item.key === activeKey ? "page" : undefined}
                  aria-keyshortcuts={item.shortcut}
                  onMouseDown={(event) => {
                    if (isInAppNavigation(event)) item.onSelect?.();
                  }}
                  onClick={(event) => {
                    if (event.detail === 0 && isInAppNavigation(event))
                      item.onSelect?.();
                  }}
                >
                  {tabContent(item)}
                </Tab>
              ) : (
                <TabButton
                  key={item.key}
                  data-tab-key={item.key}
                  className={item.key === activeKey ? "active" : undefined}
                  aria-label={item.label}
                  aria-pressed={item.key === activeKey}
                  aria-keyshortcuts={item.shortcut}
                  onPress={item.onSelect}
                >
                  {tabContent(item)}
                </TabButton>
              );
            })}
      </Row>
    </Root>
  );
}
